import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DoctorBookingType } from '../../core/enums/doctor-booking-type.enum';
import { WalkInBookingRequest } from '../../core/models/walk-in-booking-request.model';
import { DoctorSearchResponse } from '../../core/models/doctor-search-response.model';
import { BookingService } from '../../core/services/booking.service';
import { NotificationService } from '../../core/services/notification.service';
import { DoctorInvitationService } from '../../core/services/doctor-invitation.service';
import { TitleCasePipe } from '@angular/common';
import { ClinicService } from '../../core/services/clinic.service';
import { PatientService } from '../../core/services/patient.service';
import { SettingsService } from '../../core/services/settings.service';
import { OperatingHoursDto } from '../../core/models/operating-hours.model';
import { User } from '../../core/models/user.model';
import { PortalNavigation, PortalNavigationItem } from '../portal-navigation/portal-navigation';
import { DoctorProfileService } from '../../core/services/doctor-profile.service';

@Component({
  selector: 'app-create-booking',
  imports: [FormsModule, TitleCasePipe, PortalNavigation],
  templateUrl: './create-booking.html',
  styleUrl: './create-booking.scss',
})
export class CreateBooking implements OnInit {
  bookingTypes = Object.values(DoctorBookingType);

  form: WalkInBookingRequest = {
    doctorId: '',
    patientFirstName: '',
    patientLastName: '',
    patientPhone: '',
    patientId: '',
    patientCode: '',
    reasonForVisit: '',
    notes: '',
    bookingType: DoctorBookingType.WALK_IN,
    bookingSlot: {
      id: '',
      slotDate: '',
      slotTime: '',
      doctorId: '',
    },
  };

  isSubmitting = false;
  isLoadingOperatingHours = false;
  private timeout: any;

  doctorSearchTerm = '';
  doctorSearchResults: DoctorSearchResponse[] = [];
  selectedDoctor: DoctorSearchResponse | null = null;
  isSearchingDoctors = false;
  doctorSearchAttempted = false;
  userProfile: User | null = null;
  activeRole = 'DOCTOR';
  readonly clinicNavigationItems: PortalNavigationItem[] = [
    { label: 'Patients', icon: 'bi-people-fill', route: '/dashboard/patients-view' },
    { label: 'Bookings', icon: 'bi-calendar-check-fill', route: '/dashboard/booking-list' },
    { label: 'Create Booking', icon: 'bi-calendar-plus-fill', route: '/dashboard/create-booking' },
  ];
  readonly managerNavigationItems: PortalNavigationItem[] = [
    { label: 'Reporting', icon: 'bi-bar-chart-line', route: '/dashboard/reporting' },
    { label: 'User Management', icon: 'bi-people', route: '/dashboard/manage-users' },
  ];
  operatingHours: OperatingHoursDto = {
    id: '',
    weekdayOpen: '08:00',
    weekdayClose: '17:00',
    saturdayOpen: true,
    saturdayStart: '08:00',
    saturdayEnd: '13:00',
    sundayOpen: false,
    sundayStart: '09:00',
    sundayEnd: '12:00',
    holidays: [],
  };

  constructor(
    private bookingService: BookingService,
    private notificationService: NotificationService,
    private clinicService: ClinicService,
    private patientService: PatientService,
    private settingsService: SettingsService,
    private doctorInvitationService: DoctorInvitationService,
    private router: Router,
    private doctorProfileService: DoctorProfileService,
  ) {}

  ngOnInit(): void {
    this.loadUserProfile();
    this.loadDefaultDoctor();

    this.loadOperatingHours();

    let patient = history.state['patient'];
    if (!!patient) {
      this.form.patientId = patient.id || '';
      this.form.patientFirstName = patient.firstName || '';
      this.form.patientLastName = patient.lastName || '';
      this.form.patientPhone = patient.phoneNumber || '';
      this.form.patientCode = patient.patientCode || '';
    }
  }

  onDoctorIdChange(): void {
    this.form.bookingSlot.doctorId = this.form.doctorId;
  }

  private loadDefaultDoctor(): void {
    if (this.activeRole === 'DOCTOR') {
      this.doctorProfileService.getDoctorProfileDetails().subscribe({
        next: (doctor) =>
          this.selectDoctor({
            doctorId: doctor.id,
            firstName: doctor.user?.firstName ?? '',
            lastName: doctor.user?.lastName ?? '',
            email: doctor.user?.email ?? '',
            specialization: doctor.specialization ?? '',
            bio: doctor.bio ?? '',
            experienceYears: doctor.experienceYears ?? 0,
          }),
        error: () => this.notificationService.error('Failed to load your doctor profile.'),
      });
      return;
    }

    if (this.activeRole !== 'RECEPTION') return;
    this.clinicService.getClinicDoctorProfiles().subscribe({
      next: (doctors) => {
        if (doctors.length === 1) {
          const doctor = doctors[0];
          this.selectDoctor({
            doctorId: doctor.doctorId,
            firstName: doctor.firstName,
            lastName: doctor.lastName,
            email: '',
            specialization: doctor.specialization,
            bio: '',
            experienceYears: 0,
          });
        }
      },
      error: () => this.notificationService.error('Failed to load accessible doctors.'),
    });
  }

  private loadOperatingHours(): void {
    this.isLoadingOperatingHours = true;

    this.settingsService.getOperatingHours().subscribe({
      next: (operatingHours) => {
        this.operatingHours = {
          id: operatingHours.id ?? '',
          weekdayOpen: operatingHours.weekdayOpen ?? '08:00',
          weekdayClose: operatingHours.weekdayClose ?? '17:00',
          saturdayOpen: operatingHours.saturdayOpen ?? true,
          saturdayStart: operatingHours.saturdayStart ?? '08:00',
          saturdayEnd: operatingHours.saturdayEnd ?? '13:00',
          sundayOpen: operatingHours.sundayOpen ?? false,
          sundayStart: operatingHours.sundayStart ?? '09:00',
          sundayEnd: operatingHours.sundayEnd ?? '12:00',
          holidays: operatingHours.holidays ?? [],
        };
        this.isLoadingOperatingHours = false;
      },
      error: () => {
        this.isLoadingOperatingHours = false;
      },
    });
  }

  get slotTimeMin(): string {
    const minimum = this.getSelectedDayWindow().min;
    return minimum && minimum < '06:00' ? '06:00' : minimum;
  }

  get slotTimeMax(): string {
    const maximum = this.getSelectedDayWindow().max;
    return maximum && maximum > '20:00' ? '20:00' : maximum;
  }

  get minimumBookingDate(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get slotTimeRangeLabel(): string {
    const window = this.getSelectedDayWindow();

    if (!window.allowed) {
      return window.message;
    }

    return `Allowed time range: ${window.min} - ${window.max}`;
  }

  get slotDateValidationMessage(): string {
    return this.getSelectedDayWindow().message;
  }

  get slotTimeValidationMessage(): string {
    const window = this.getSelectedDayWindow();
    if (!window.allowed) {
      return window.message;
    }
    return `Time must be between ${window.min} and ${window.max}.`;
  }

  isSlotDateAllowed(): boolean {
    return this.getSelectedDayWindow().allowed;
  }

  isSlotTimeWithinOperatingHours(): boolean {
    const slotTime = this.form.bookingSlot.slotTime;
    const window = this.getSelectedDayWindow();

    if (!slotTime || !window.allowed) {
      return window.allowed;
    }

    return slotTime >= this.slotTimeMin && slotTime <= this.slotTimeMax;
  }

  isBookingScheduleValid(): boolean {
    return this.isSlotDateAllowed() && this.isSlotTimeWithinOperatingHours();
  }

  private getSelectedDayWindow(): { allowed: boolean; min: string; max: string; message: string } {
    const slotDate = this.form.bookingSlot.slotDate;

    if (!slotDate) {
      return {
        allowed: true,
        min: this.operatingHours.weekdayOpen || '08:00',
        max: this.operatingHours.weekdayClose || '17:00',
        message: `Allowed time range: ${this.operatingHours.weekdayOpen || '08:00'} - ${this.operatingHours.weekdayClose || '17:00'}`,
      };
    }

    const selectedDate = new Date(`${slotDate}T00:00:00`);
    if (isNaN(selectedDate.getTime())) {
      return {
        allowed: false,
        min: '',
        max: '',
        message: 'Please select a valid booking date.',
      };
    }

    if (slotDate < this.minimumBookingDate) {
      return {
        allowed: false,
        min: '',
        max: '',
        message: 'Past dates cannot be booked.',
      };
    }

    const selectedDateKey = slotDate.slice(0, 10);
    const holiday = this.operatingHours.holidays.find(
      (item) => (item.date ?? '').slice(0, 10) === selectedDateKey,
    );
    if (holiday) {
      return {
        allowed: false,
        min: '',
        max: '',
        message: `${holiday.name || 'Selected day'} is marked as a holiday and cannot be booked.`,
      };
    }

    const day = selectedDate.getDay();

    if (day === 6) {
      if (!this.operatingHours.saturdayOpen) {
        return {
          allowed: false,
          min: '',
          max: '',
          message: 'Saturday bookings are disabled in the practice settings.',
        };
      }

      return {
        allowed: true,
        min: this.operatingHours.saturdayStart || '08:00',
        max: this.operatingHours.saturdayEnd || '13:00',
        message: `Allowed time range: ${this.operatingHours.saturdayStart || '08:00'} - ${this.operatingHours.saturdayEnd || '13:00'}`,
      };
    }

    if (day === 0) {
      if (!this.operatingHours.sundayOpen) {
        return {
          allowed: false,
          min: '',
          max: '',
          message: 'Sunday bookings are disabled in the practice settings.',
        };
      }

      return {
        allowed: true,
        min: this.operatingHours.sundayStart || '09:00',
        max: this.operatingHours.sundayEnd || '12:00',
        message: `Allowed time range: ${this.operatingHours.sundayStart || '09:00'} - ${this.operatingHours.sundayEnd || '12:00'}`,
      };
    }

    return {
      allowed: true,
      min: this.operatingHours.weekdayOpen || '08:00',
      max: this.operatingHours.weekdayClose || '17:00',
      message: `Allowed time range: ${this.operatingHours.weekdayOpen || '08:00'} - ${this.operatingHours.weekdayClose || '17:00'}`,
    };
  }

  searchDoctors(): void {
    const term = this.doctorSearchTerm.trim();
    if (!term) return;
    this.isSearchingDoctors = true;
    this.doctorSearchAttempted = true;
    this.doctorSearchResults = [];
    this.clinicService.searchDoctors(term).subscribe({
      next: (results: DoctorSearchResponse[]) => {
        this.isSearchingDoctors = false;
        this.doctorSearchResults = results;
      },
      error: () => {
        this.isSearchingDoctors = false;
        this.notificationService.error('Failed to search doctors.');
      },
    });
  }

  selectDoctor(doctor: DoctorSearchResponse): void {
    this.selectedDoctor = doctor;
    this.form.doctorId = doctor.doctorId;
    this.form.bookingSlot.doctorId = doctor.doctorId;
    this.doctorSearchResults = [];
    this.doctorSearchTerm = '';
  }

  clearDoctorSelection(): void {
    this.selectedDoctor = null;
    this.form.doctorId = '';
    this.form.bookingSlot.doctorId = '';
    this.doctorSearchResults = [];
    this.doctorSearchTerm = '';
    this.doctorSearchAttempted = false;
  }

  errorMessage: string | null = null;

  submit(): void {
    this.errorMessage = null;
    if (!this.isBookingScheduleValid()) {
      const msg = this.slotDateValidationMessage;
      this.errorMessage = msg;
      this.notificationService.error(msg, 'Invalid Schedule');
      return;
    }

    this.isSubmitting = true;
    this.bookingService.createWalkInBooking(this.form).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.notificationService.success(
          'Walk-in booking created successfully!',
          'Booking Confirmed',
        );
        this.router.navigate(['/dashboard/booking-list']);
      },
      error: (err) => {
        this.isSubmitting = false;
        const msg =
          err?.error?.message ??
          'Failed to create booking. Please check doctor availability and try again.';
        this.errorMessage = msg;
        this.notificationService.error(msg, 'Booking Failed');
      },
    });
  }

  onSearch(): void {
    clearTimeout(this.timeout);

    this.timeout = setTimeout(() => {
      this.patientService.getPatientByCode(this.form.patientCode).subscribe((patient) => {
        this.form.patientId = patient.id || '';
        this.form.patientFirstName = patient ? patient.firstName : '';
        this.form.patientLastName = patient ? patient.lastName : '';
        this.form.patientPhone = patient ? patient.phoneNumber : '';
        this.form.patientCode = patient ? patient.patientCode : '';
      });
    }, 500);
  }

  cancel(): void {
    this.router.navigate(['/dashboard/booking-list']);
  }

  get portalName(): string {
    return `${this.activeRole.charAt(0)}${this.activeRole.slice(1).toLowerCase()} Portal`;
  }

  get portalIcon(): string {
    return this.activeRole === 'MANAGER'
      ? 'bi-clipboard2-pulse-fill'
      : this.activeRole === 'RECEPTION'
        ? 'bi-hospital-fill'
        : 'bi-heart-pulse-fill';
  }

  get portalClass(): string {
    return this.activeRole === 'MANAGER' ? 'text-danger' : 'text-primary';
  }

  get profileRoute(): string {
    return this.activeRole === 'DOCTOR' ? '/dashboard/doctor-profile' : '/dashboard/profile';
  }

  get primaryNavigationItems(): PortalNavigationItem[] {
    return this.activeRole === 'MANAGER' ? [] : this.clinicNavigationItems;
  }

  get additionalNavigationItems(): PortalNavigationItem[] {
    return this.activeRole === 'MANAGER' ? this.managerNavigationItems : [];
  }

  private loadUserProfile(): void {
    const storedProfile = localStorage.getItem('userProfile');
    this.activeRole = localStorage.getItem('userRole') ?? 'DOCTOR';

    if (!storedProfile) {
      return;
    }

    try {
      this.userProfile = JSON.parse(storedProfile) as User;
    } catch {
      this.userProfile = null;
    }
  }
}
